import { Flex, Text, Card, Heading } from '@radix-ui/themes';

import { Signal, Fingerprint, SignalType } from '../../types';
import { FingerprintList } from './FingerprintList';

export interface SignalEditorProps {
  signal: Signal;
  onChange: (signal: Signal) => void;
  showHeader?: boolean;
}

const SignalEditor = ({
  signal,
  onChange,
  showHeader = true,
}: SignalEditorProps) => {
  const handleAddFingerprint = (newFingerprint: Fingerprint) => {
    const updatedSignal = {
      ...signal,
      fingerprints: [...signal.fingerprints, newFingerprint],
    };
    onChange(updatedSignal);
  };

  const handleEditFingerprint = (
    index: number,
    updatedFingerprint: Fingerprint,
  ) => {
    const updatedFingerprints = [...signal.fingerprints];
    updatedFingerprints[index] = updatedFingerprint;

    const updatedSignal = {
      ...signal,
      fingerprints: updatedFingerprints,
    };
    onChange(updatedSignal);
  };

  const handleDeleteFingerprint = (index: number) => {
    const updatedFingerprints = signal.fingerprints.filter(
      (_: Fingerprint, i: number) => i !== index,
    );

    const updatedSignal = {
      ...signal,
      fingerprints: updatedFingerprints,
    };
    onChange(updatedSignal);
  };

  // Check if the signal type supports fingerprints
  const supportsFingerprints =
    signal.signalType === SignalType.Transaction ||
    signal.signalType === SignalType.Log ||
    signal.signalType === SignalType.Block;

  return (
    <Flex direction="column" gap="3">
      {showHeader && (
        <Heading as="h3" size="3" weight="medium">
          Conditions
        </Heading>
      )}

      {supportsFingerprints ? (
        <FingerprintList
          fingerprints={signal.fingerprints}
          signalType={signal.signalType}
          onAdd={handleAddFingerprint}
          onEdit={handleEditFingerprint}
          onDelete={handleDeleteFingerprint}
        />
      ) : (
        <Card variant="surface">
          <Text size="2" color="gray">
            {signal.signalType === SignalType.Network
              ? 'Network signals monitor network-level events. No additional conditions are needed.'
              : 'This signal type does not support conditions.'}
          </Text>
        </Card>
      )}

      {supportsFingerprints && signal.fingerprints.length === 0 && (
        <Text size="1" color="gray">
          Add conditions to filter which {signal.signalType.toLowerCase()}s
          trigger this signal.
        </Text>
      )}
    </Flex>
  );
};

export default SignalEditor;
